import React, { useState } from 'react';
import { useSearchParams, useNavigate, useParams } from 'react-router-dom';
import { Loader2, ArrowLeft, Search, AlertCircle, RefreshCw } from 'lucide-react';
import { scanApi } from '@/services/api';
import { BrandRiskAnalysis } from '@/types';
import { useDomainScan } from '@/hooks/useDomainScan';
import { usePriceHistory } from '@/hooks/usePriceHistory';
import DomainValuation from '@/components/DomainValuation';
import BrandRiskAnalysisComponent from '@/components/BrandRiskAnalysis';
import DomainPricing from '@/components/DomainPricing';
import PriceSparkline from '@/components/PriceSparkline';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import SEO from '@/components/SEO';
import ValuationInfo from '@/components/ValuationInfo';

function formatRelativeTime(date: Date | string): string {
  const ms = Date.now() - new Date(date).getTime();
  const mins = Math.floor(ms / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

const DomainDetail: React.FC = () => {
  const { domain: urlParam } = useParams<{ domain: string }>();
  const [searchParams] = useSearchParams();
  // Support both /domain/google.com (new) and /domain?q=google.com (legacy)
  const rawDomain = urlParam || searchParams.get('q') || searchParams.get('domain') || '';
  const domain = rawDomain ? decodeURIComponent(rawDomain) : '';
  const navigate = useNavigate();
  const [brandRiskLoading, setBrandRiskLoading] = useState(false);
  const [brandRiskError, setBrandRiskError] = useState<string | null>(null);
  const [brandRiskAnalysis, setBrandRiskAnalysis] = useState<BrandRiskAnalysis | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const { data: result, isLoading: loading, error: scanError, forceRefresh } = useDomainScan(domain);
  const { data: priceHistory } = usePriceHistory(domain);

  const error = scanError ? (scanError instanceof Error ? scanError.message : 'Failed to scan domain') : null;

  const handleForceRefresh = async () => {
    setIsRefreshing(true);
    try {
      await forceRefresh();
      // Clear any cached brand risk state so the freshly-fetched result.brandRisk shows
      setBrandRiskAnalysis(null);
      setBrandRiskError(null);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleScanVariants = () => {
    if (domain) {
      // Extract keyword from domain (remove TLD)
      const keyword = domain.split('.')[0];
      navigate(`/?q=${encodeURIComponent(keyword)}`);
    }
  };

  const handleAnalyzeBrandRisk = async () => {
    if (!domain) return;

    setBrandRiskLoading(true);
    setBrandRiskError(null);

    try {
      const analysis = await scanApi.analyzeBrandRisk(
        domain,
        (_step, _progress) => {},
        (completedAnalysis) => {
          // Analysis completed
          setBrandRiskAnalysis(completedAnalysis);
          setBrandRiskLoading(false);
        },
        (errorMessage) => {
          // Error occurred
          setBrandRiskError(errorMessage);
          setBrandRiskLoading(false);
        }
      );

      // If analysis is returned directly (non-streaming)
      if (analysis) {
        setBrandRiskAnalysis(analysis);
        setBrandRiskLoading(false);
      }
    } catch (err) {
      setBrandRiskError(err instanceof Error ? err.message : 'Failed to analyze brand risk');
      setBrandRiskLoading(false);
    }
  };

  const handleRetryBrandRisk = () => {
    handleAnalyzeBrandRisk();
  };

  if (loading) {
    return (
      <>
        <SEO
          title={`Scanning ${domain} - DomBase`}
          description={`Scanning domain ${domain} for availability, pricing, and detailed information.`}
        />
        <div className="bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <Button
              variant="outline"
              onClick={() => navigate(-1)}
              className="mb-6"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>

            {/* Skeleton Loading */}
            <div className="space-y-6">
              <div className="animate-pulse space-y-6">
                <div className="h-8 bg-gray-100 rounded w-1/3"></div>
                <div className="h-4 bg-gray-100 rounded w-1/4"></div>
                <div className="space-y-4">
                  <div className="h-32 bg-gray-100 rounded"></div>
                  <div className="h-32 bg-gray-100 rounded"></div>
                  <div className="h-32 bg-gray-100 rounded"></div>
                </div>
              </div>
              <div className="flex items-center justify-center mt-8">
                <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
                <span className="ml-3 text-gray-500">Scanning domain...</span>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (error || !result) {
    return (
      <>
        <SEO
          title={`Error - ${domain} - DomBase`}
          description={`Error scanning domain ${domain}.`}
        />
        <div className="bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <Button
              variant="outline"
              onClick={() => navigate(-1)}
              className="mb-6"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <div className="rounded-lg border border-gray-200 p-8">
              <div className="text-center">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Error</h2>
                <p className="text-gray-500">{error || 'Domain not found'}</p>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  const statusColors = {
    available: 'bg-green-100 text-green-800',
    registered: 'bg-red-100 text-red-800',
    error: 'bg-yellow-100 text-yellow-800'
  };

  // Generate Open Graph image URL (you can create dynamic OG images later)
  const ogImage = `/og-domain.png`; // Default OG image, can be made dynamic
  const domainUrl = typeof window !== 'undefined'
    ? `${window.location.origin}/domain/${encodeURIComponent(result.domain)}`
    : `https://dombase.xyz/domain/${encodeURIComponent(result.domain)}`;

  // Generate rich description with key information
  const generateDescription = () => {
    let desc = `Complete domain analysis for ${result.domain}. `;
    if (result.status === 'available') {
      desc += `This domain is available for registration. `;
    } else {
      desc += `Domain status: ${result.status}. `;
    }
    if (result.pricingSources && result.pricingSources.length > 0) {
      const prices = result.pricingSources.map(p => p.purchasePrice).filter(p => p !== null);
      if (prices.length > 0) {
        const minPrice = Math.min(...prices as number[]);
        desc += `Starting at $${minPrice.toFixed(2)}. `;
      }
    }
    if (result.valuation) {
      desc += `Estimated value: $${result.valuation.estimatedValue.min.toLocaleString()} - $${result.valuation.estimatedValue.max.toLocaleString()}. `;
    }
    desc += `Get instant pricing comparison, WHOIS data, DNS information, and brand risk analysis.`;
    return desc;
  };

  // Compute data availability flags
  const whois = result.whoisData;
  const hasWhoisData = !!(
    whois &&
    (
      whois.registrar ||
      whois.creationDate ||
      whois.expirationDate ||
      whois.lastUpdated ||
      (whois.nameServers && whois.nameServers.length > 0) ||
      whois.registrant
    )
  );
  const dns = result.dnsData;
  const hasDnsData = !!(
    dns &&
    (
      (dns.ipAddresses && dns.ipAddresses.length > 0) ||
      (dns.mxRecords && dns.mxRecords.length > 0) ||
      (dns.txtRecords && dns.txtRecords.length > 0)
    )
  );
  return (
    <>
      <SEO
        title={`${result.domain} - Domain Details & Pricing Comparison - DomBase`}
        description={generateDescription()}
        canonical={`/domain/${encodeURIComponent(result.domain)}`}
        ogTitle={`${result.domain} - Domain Analysis & Pricing`}
        ogDescription={generateDescription()}
        ogUrl={domainUrl}
        ogImage={ogImage}
        ogType="website"
        twitterTitle={`${result.domain} - Domain Analysis & Pricing`}
        twitterDescription={generateDescription()}
        twitterCard="summary_large_image"
        twitterImage={ogImage}
      />
      {/*
        Determine if we actually have WHOIS or DNS data to show.
        We hide these sections entirely when there is no data.
      */}
      {(() => {
        const hasWhoisData =
          !!result.whoisData &&
          !!(
            result.whoisData.registrar ||
            result.whoisData.creationDate ||
            result.whoisData.expirationDate ||
            result.whoisData.lastUpdated ||
            (result.whoisData.nameServers && result.whoisData.nameServers.length > 0) ||
            result.whoisData.registrant
          );
        const hasDnsData =
          !!result.dnsData &&
          !!(
            (result.dnsData.ipAddresses && result.dnsData.ipAddresses.length > 0) ||
            (result.dnsData.mxRecords && result.dnsData.mxRecords.length > 0) ||
            (result.dnsData.txtRecords && result.dnsData.txtRecords.length > 0)
          );
        // Store flags on window-scoped variables to reuse below without re-evaluating IIFE
        (window as any).__hasWhoisData = hasWhoisData;
        (window as any).__hasDnsData = hasDnsData;
        return null;
      })()}
      <div className="bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Button
            variant="outline"
            onClick={() => navigate(-1)}
            className="mb-6"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>

          <div className="space-y-8">
            {/* Domain Header */}
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <h1 className="text-3xl font-bold mb-2">
                  {(() => {
                    const idx = result.domain.indexOf('.');
                    if (idx === -1) return <span className="text-gray-900">{result.domain}</span>;
                    return (
                      <>
                        <span className="text-gray-400 font-medium">{result.domain.slice(0, idx)}</span>
                        <span className="text-gray-900">{result.domain.slice(idx)}</span>
                      </>
                    );
                  })()}
                </h1>
                <div className="flex items-center gap-3 flex-wrap">
                  <Badge className={statusColors[result.status]}>
                    {result.status.charAt(0).toUpperCase() + result.status.slice(1)}
                  </Badge>
                  <span className="text-xs text-gray-400 flex items-center gap-1.5">
                    Last scanned {formatRelativeTime(result.scannedAt)}
                    <button
                      onClick={handleForceRefresh}
                      disabled={isRefreshing}
                      className="inline-flex items-center gap-1 text-blue-500 hover:text-blue-700 disabled:opacity-50 transition-colors"
                      title="Force rescan"
                    >
                      <RefreshCw className={`h-3 w-3 ${isRefreshing ? 'animate-spin' : ''}`} />
                      Refresh
                    </button>
                  </span>
                </div>
              </div>
              {result.status === 'registered' && (
                <Button
                  onClick={handleScanVariants}
                  className="bg-blue-500 hover:bg-blue-600 text-white transition-all duration-200 hover:scale-105 active:scale-95"
                  size="lg"
                >
                  <Search className="h-4 w-4 mr-2" />
                  Explore Available Alternatives
                </Button>
              )}
              {result.status === 'available' && (
                <Button
                  onClick={handleScanVariants}
                  className="bg-blue-500 hover:bg-blue-600 text-white transition-all duration-200 hover:scale-105 active:scale-95"
                  size="lg"
                >
                  <Search className="h-4 w-4 mr-2" />
                  Find Similar Domains
                </Button>
              )}
            </div>

            {/* Pricing Comparison */}
            <div className="border-t border-gray-100 pt-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                Pricing Comparison
              </h2>
              {(() => {
                // Show comparison table if we have pricing sources, or single pricing with showDetails
                if (result.pricingSources && result.pricingSources.length > 0) {
                  return <DomainPricing pricing={result.pricing} pricingSources={result.pricingSources} showDetails={true} size="lg" />;
                } else if (result.pricing) {
                  return <DomainPricing pricing={result.pricing} pricingSources={[result.pricing]} showDetails={true} size="lg" />;
                } else {
                  return (
                    <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-500">
                      Pricing information is not available for this domain at the moment.
                    </div>
                  );
                }
              })()}
              {priceHistory && (
                <div className="mt-4">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Price History</p>
                  <PriceSparkline
                    points={priceHistory.points}
                    changeLabel={priceHistory.changeLabel}
                    changePercent={priceHistory.changePercent}
                  />
                </div>
              )}
            </div>

            {/* Smart Valuation */}
            {result.valuation && (
              <div className="border-t border-gray-100 pt-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  Smart Valuation
                  <ValuationInfo />
                </h2>
                <DomainValuation valuation={result.valuation} domain={result.domain} />
              </div>
            )}

            {/* Brand Risk Analysis */}
            {(() => {
              // Prefer freshly-fetched analysis (from button click), fall back to cached result from DB
              const displayedAnalysis = brandRiskAnalysis ?? result.brandRisk ?? null;
              const isCachedFromDb = !brandRiskAnalysis && !!result.brandRisk;
              return (
                <div className="border-t border-gray-100 pt-6">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h2 className="text-xl font-semibold text-gray-900">Brand Risk Analysis</h2>
                      {isCachedFromDb && result.brandRisk?.analyzedAt && (
                        <p className="text-xs text-gray-400 mt-0.5">
                          Analyzed {formatRelativeTime(result.brandRisk.analyzedAt as string)}
                        </p>
                      )}
                    </div>
                    {!brandRiskLoading && (
                      <Button
                        onClick={handleAnalyzeBrandRisk}
                        variant="outline"
                        className="bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-200"
                      >
                        {displayedAnalysis ? (
                          <>
                            <RefreshCw className="h-4 w-4 mr-2" />
                            Re-analyze
                          </>
                        ) : (
                          <>
                            <AlertCircle className="h-4 w-4 mr-2" />
                            Analyze Brand Risk
                          </>
                        )}
                      </Button>
                    )}
                  </div>

                  {brandRiskLoading && (
                    <div className="bg-gray-50 rounded-lg p-6 text-center">
                      <Loader2 className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-3" />
                      <p className="text-gray-500 text-sm">
                        Analyzing brand risk... This may take a few moments.
                      </p>
                    </div>
                  )}

                  {brandRiskError && !brandRiskLoading && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                        <div className="flex-1">
                          <p className="text-red-800 font-medium mb-1">Analysis Failed</p>
                          <p className="text-red-700 text-sm mb-3">{brandRiskError}</p>
                          <Button
                            onClick={handleRetryBrandRisk}
                            variant="outline"
                            size="sm"
                            className="bg-red-50 hover:bg-red-100 text-red-700 border-red-200"
                          >
                            <RefreshCw className="h-3 w-3 mr-2" />
                            Retry Analysis
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}

                  {displayedAnalysis && !brandRiskLoading && !brandRiskError && (
                    <BrandRiskAnalysisComponent brandRisk={displayedAnalysis} domain={result.domain} />
                  )}

                  {!displayedAnalysis && !brandRiskLoading && !brandRiskError && (
                    <div className="bg-gray-50 rounded-lg p-6 text-center">
                      <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                      <p className="text-gray-500 text-sm mb-4">
                        Brand risk analysis has not been performed yet. Click the button above to analyze this domain.
                      </p>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* WHOIS Data */}
            {hasWhoisData && (
              <div className="border-t border-gray-100 pt-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  WHOIS Information
                </h2>
                <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
                  {whois?.registrar && (
                    <div>
                      <span className="font-semibold text-gray-900">Registrar:</span>{' '}
                      <span className="text-gray-700">{whois?.registrar}</span>
                    </div>
                  )}
                  {whois?.creationDate && (
                    <div>
                      <span className="font-semibold text-gray-900">Creation Date:</span>{' '}
                      <span className="text-gray-700">
                        {new Date(whois?.creationDate as any).toLocaleDateString()}
                      </span>
                    </div>
                  )}
                  {whois?.expirationDate && (
                    <div>
                      <span className="font-semibold text-gray-900">Expiration Date:</span>{' '}
                      <span className="text-gray-700">
                        {new Date(whois?.expirationDate as any).toLocaleDateString()}
                      </span>
                    </div>
                  )}
                  {whois?.lastUpdated && (
                    <div>
                      <span className="font-semibold text-gray-900">Last Updated:</span>{' '}
                      <span className="text-gray-700">
                        {new Date(whois?.lastUpdated as any).toLocaleDateString()}
                      </span>
                    </div>
                  )}
                  {whois?.nameServers && whois.nameServers.length > 0 && (
                    <div>
                      <span className="font-semibold text-gray-900">Name Servers:</span>
                      <div className="mt-1 space-y-1">
                        {whois.nameServers.map((ns, idx) => (
                          <div key={idx} className="font-mono text-gray-700 bg-white px-2 py-1 rounded">
                            {ns}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {whois?.registrant && (
                    <div className="mt-3 pt-3 border-t border-gray-100">
                      <span className="font-semibold text-gray-900">Registrant Information:</span>
                      <div className="mt-1 space-y-1 pl-4">
                        {whois?.registrant?.name && (
                          <div>
                            <span className="font-semibold text-gray-700">Name:</span>{' '}
                            <span className="text-gray-700">{whois?.registrant?.name}</span>
                          </div>
                        )}
                        {whois?.registrant?.organization && (
                          <div>
                            <span className="font-semibold text-gray-700">Organization:</span>{' '}
                            <span className="text-gray-700">{whois?.registrant?.organization}</span>
                          </div>
                        )}
                        {whois?.registrant?.email && (
                          <div>
                            <span className="font-semibold text-gray-700">Email:</span>{' '}
                            <span className="text-gray-700">{whois?.registrant?.email}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* DNS Data */}
            {hasDnsData && (
              <div className="border-t border-gray-100 pt-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  DNS Information
                </h2>
                <div className="bg-gray-50 rounded-lg p-4 space-y-3 text-sm">
                  {dns?.ipAddresses && dns.ipAddresses.length > 0 && (
                    <div>
                      <span className="font-semibold text-gray-900">A Records (IP Addresses):</span>
                      <div className="mt-1 space-y-1">
                        {dns.ipAddresses.map((ip, idx) => (
                          <div key={idx} className="font-mono text-gray-700 bg-white px-2 py-1 rounded">
                            {ip}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {dns?.mxRecords && dns.mxRecords.length > 0 && (
                    <div>
                      <span className="font-semibold text-gray-900">MX Records (Mail Servers):</span>
                      <div className="mt-1 space-y-1">
                        {dns.mxRecords.map((mx, idx) => (
                          <div key={idx} className="font-mono text-gray-700 bg-white px-2 py-1 rounded">
                            {mx}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {dns?.txtRecords && dns.txtRecords.length > 0 && (
                    <div>
                      <span className="font-semibold text-gray-900">TXT Records:</span>
                      <div className="mt-1 space-y-1">
                        {dns.txtRecords.map((txt, idx) => (
                          <div key={idx} className="font-mono text-gray-700 bg-white px-2 py-1 rounded break-all">
                            {txt}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Error Message */}
            {result.error && (
              <div className="border-t border-gray-100 pt-6">
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <p className="text-yellow-800 text-sm">{result.error}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default DomainDetail;

